import { uimetadata } from '../metadata';
import {
  AutoIntelSDK,
  BrowsingSession,
  UserContext,
  ConfigContext,
  SourceContext,
  InventoryRecommendationRequest,
  InventoryRecommendation,
  InteractionAction,
  InteractionPayload,
  ConfigurationRecommendationResponse,
} from './autointel.types';

// SDK configuration - populate with actual credentials
const SDK_CONFIG = {
  tenant: 'gm-arabia-dev',
  username: 'metadome-dev',
  apiKey: 'EwtJM0jidUmCiorOFcHQD5y8Hgm21pD0',
  environment: 'dev' as const, // 'dev' | 'prod'
};

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Get the AutoIntel SDK instance
 */
const getSDK = () => window.ai;

/**
 * Initialize the AutoIntel SDK
 */
export const initAutoIntel = async (): Promise<void> => {
  if (isInitialized) return;
  if (initializationPromise) return initializationPromise;

  initializationPromise = new Promise((resolve, reject) => {
    const maxRetries = 100; // 10 seconds with 100ms interval
    let retries = 0;

    const checkSDK = async () => {
      const sdk = getSDK();

      if (sdk) {
        try {
          // Enable staging for dev environment
          if (SDK_CONFIG.environment === 'dev') {
            sdk.enableStaging();
            sdk.setAuthEnvironment('dev');
          }

          // Await setCredentials to ensure SDK is fully configured
          await window.ai?.setCredentials(
            SDK_CONFIG.tenant,
            SDK_CONFIG.username,
            SDK_CONFIG.apiKey
          );

          window.ai?.setUser({ auto: false });
          isInitialized = true;
          console.log('[AutoIntel] SDK initialized successfully');
          resolve();
        } catch (error) {
          // `window.ai` can exist as a stub before its methods are fully
          // wired up (same class of race as waitForSDKNamespace below), so
          // a truthy `sdk` calling enableStaging/setCredentials can still
          // throw on the first few checks. Retry like the "not loaded yet"
          // branch instead of failing permanently on the first hiccup.
          retries++;
          if (retries >= maxRetries) {
            console.error('[AutoIntel] SDK initialization failed:', error);
            initializationPromise = null;
            reject(error);
          } else {
            setTimeout(checkSDK, 100);
          }
        }
      } else {
        retries++;
        if (retries >= maxRetries) {
          const error = new Error(
            '[AutoIntel] SDK failed to load within timeout'
          );
          console.error(error.message);
          initializationPromise = null;
          reject(error);
        } else {
          // SDK not loaded yet, retry
          setTimeout(checkSDK, 100);
        }
      }
    };

    // Start checking for SDK
    checkSDK();
  });

  return await initializationPromise;
};

/**
 * Check if the SDK is available
 */
export const isSDKAvailable = (): boolean => {
  return isInitialized && !!getSDK();
};

/**
 * `window.ai` can become truthy before all of its sub-namespaces (e.g.
 * `.inventory`, `.app`) are attached — the SDK populates them
 * asynchronously after its own internal bootstrap, on a timeline this app
 * has no hook into. A page that reaches its first recommendation call
 * quickly (little else to load) can race ahead of that and crash with
 * "Cannot read properties of undefined (reading 'getRecommendation')" even
 * though `sdk` itself is defined. Poll for the specific namespace instead
 * of assuming `sdk` truthy means everything under it is ready.
 */
const waitForSDKNamespace = <T,>(
  getNamespace: () => T | undefined,
  maxRetries = 50,
  intervalMs = 100
): Promise<T | undefined> => {
  return new Promise((resolve) => {
    let retries = 0;
    const check = () => {
      const ns = getNamespace();
      if (ns) {
        resolve(ns);
        return;
      }
      retries++;
      if (retries >= maxRetries) {
        resolve(undefined);
        return;
      }
      setTimeout(check, intervalMs);
    };
    check();
  });
};

/**
 * Generate or retrieve session ID
 */
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('autoIntelSessionId');
  if (!sessionId) {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    sessionId = `session_${Date.now()}_${array[0].toString(
      36
    )}${array[1].toString(36)}`;
    sessionStorage.setItem('autoIntelSessionId', sessionId);
  }
  return sessionId;
};

/**
 * Get user ID from AutoIntel SDK
 */
const getUserId = (): string => {
  const sdk = getSDK();
  if (sdk?.user) {
    return sdk?.user?.id;
  }

  // Fallback to sessionStorage if SDK user not available
  let userId = sessionStorage.getItem('autoIntelUserId');
  if (!userId) {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    const randomPart = `${array[0].toString(36)}${array[1].toString(
      36
    )}`.substr(0, 9);
    userId = `user_${Date.now()}_${randomPart}`;
    sessionStorage.setItem('autoIntelUserId', userId);
  }
  return userId;
};

/**
 * Get zipcode from URL parameters or default
 */
const getZipcode = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('zipcode') || urlParams.get('zip') || '00000';
};

/**
 * Build markdown-compliant context for requests
 */
const buildMarkdownContext = (config?: Partial<ConfigContext>) => {
  // Only include fields shown in markdown
  return {
    browsingSession: {
      id: getSessionId(),
      // zipcode is not in markdown, so omit
      // referrer: document.referrer || undefined,
      // landingPage: window.location.href,
    },
    user: {
      id: getUserId(),
    },
    source: {
      name: '3D Configurator',
      url: window.location.href,
    },
    config: config,
  };
};

/**
 * Get inventory recommendations for LocalMatches
 */
export const getInventoryRecommendations = async (
  productId: string,
  features: string[],
  count = 10,
  dealerIds?: string[]
): Promise<InventoryRecommendation[]> => {
  try {
    await initAutoIntel();
  } catch (error) {
    console.warn('[AutoIntel] SDK not initialized, returning empty results');
    return [];
  }

  const sdk = getSDK();

  if (!sdk) {
    console.warn('[AutoIntel] SDK not available');
    return [];
  }

  // `sdk` can be truthy before `sdk.inventory` is attached (see
  // waitForSDKNamespace above) — wait for the namespace itself.
  const inventory = await waitForSDKNamespace(() => sdk.inventory);
  if (!inventory) {
    console.warn('[AutoIntel] SDK inventory namespace not available');
    return [];
  }

  try {
    // Build context as per markdown
    const contextData = buildMarkdownContext({
      id: undefined, // Only include if present in config
      productId,
      features,
    });

    // Only include fields shown in markdown
    const request: any = {
      browsingSession: contextData.browsingSession,
      user: contextData.user,
      filter: {
        config: {
          features,
          model: {
            name: [productId],

          },
        },
        count,
        ...(dealerIds && dealerIds.length > 0 ? { dealerIds } : {}),
      },
    };

    // console.log(
    //   '[AutoIntel] getInventoryRecommendations - Request config:',
    //   JSON.stringify(request, null, 2)
    // );

    const response = await inventory.getRecommendation(request);

    // console.log(
    //   '[AutoIntel] getInventoryRecommendations - Raw Response Type:',
    //   typeof response,
    //   'isArray:',
    //   Array.isArray(response)
    // );
    // console.log(
    //   '[AutoIntel] getInventoryRecommendations - Raw Response keys:',
    //   response ? Object.keys(response) : 'none'
    // );

    // Handle both array and object responses (some SDK versions differ)
    const recommendations = Array.isArray(response)
      ? response
      : (response as any)?.recommendations || (response as any)?.data || [];

    // console.log(
    //   '[AutoIntel] getInventoryRecommendations - Extracted recommendations:',
    //   JSON.stringify(recommendations, null, 2)
    // );

    // console.log(
    //   '[AutoIntel] getInventoryRecommendations - Received:',
    //   recommendations.length,
    //   'recommendations'
    // );
    return recommendations;
  } catch (error) {
    console.error(
      '[AutoIntel] Failed to get inventory recommendations:',
      error
    );
    return [];
  }
};

/**
 * Get configuration recommendations
 */
export const getConfigurationRecommendations = async (
  modelIds: string[],
  count = 1
): Promise<ConfigurationRecommendationResponse | null> => {
  try {
    await initAutoIntel();
  } catch (error) {
    console.warn('[AutoIntel] SDK not initialized, returning null');
    return null;
  }

  const sdk = getSDK();

  if (!sdk) {
    console.warn('[AutoIntel] SDK not available');
    return null;
  }

  try {
    // Build context as per markdown
    const contextData = buildMarkdownContext();

    // Only include fields shown in markdown
    const request: any = {
      browsingSession: contextData.browsingSession,
      user: contextData.user,
      context: {
        productType: 'configuration',
      },
      source: contextData.source,
      filter: {
        count: 1,
        modelIds: [(uimetadata as any)?.config?.model].filter(Boolean) as string[],
      },
    };

    // console.log(
    //   '[AutoIntel] getConfigurationRecommendations - Request config:',
    //   JSON.stringify(request, null, 2)
    // );

    const response = await sdk.app.getRecommendation({
      recommendationContext: request,
    });
  //   console.log(
  //     '[AutoIntel] getConfigurationRecommendations - Received:',
  //     response?.recommendations?.length || 0,
  //     'recommendations'
  //   );
  //   return response;
  // } catch (error) {
  //   console.error(
  //     '[AutoIntel] Failed to get configuration recommendations:',
  //     error
  //   );
    return null;
  }
  catch{
    console.log("[AutoIntel] getRecommendation fail")
  }
};

/**
 * Post an interaction to the AutoIntel SDK
 */
export const postInteraction = async (
  itemId: string,
  action: InteractionAction,
  subAction: string,
  configContext?: Partial<ConfigContext>
): Promise<void> => {
  try {
    await initAutoIntel();
  } catch (error) {
    console.warn('[AutoIntel] SDK not initialized, skipping interaction');
    return;
  }

  const sdk = getSDK();

  if (!sdk) {
    console.warn('[AutoIntel] SDK not available');
    return;
  }

  const app = await waitForSDKNamespace(() => sdk.app);
  if (!app) {
    console.warn('[AutoIntel] SDK app namespace not available');
    return;
  }

  const payload: InteractionPayload = {
    interaction: {
      itemId,
      context: buildMarkdownContext(configContext),
      action,
      subAction,
      timestamp: Date.now(),
    },
  };

  try {
    await app.postInteraction(payload);
    console.log(
      '[AutoIntel] postInteraction - Success:',
      action,
      subAction,
      itemId
    );
  } catch (error) {
    console.error('[AutoIntel] Failed to post interaction:', error);
  }
};

// Helper function to get the autoIntelRPOCode from UI metadata based on item ID.
// `variantId` is optional but REQUIRED to resolve interior colours correctly: an
// interior colour's RPO depends on the model (variant) and seat trim, and the same
// colour id is reused across models with different codes (see model.interiorColorRPO).
export const getAutoIntelRPOCode = (itemId: string, variantId?: string): string | undefined => {
  const metadata = uimetadata as Record<string, any>;
  if (variantId) {
    for (const modelKey in metadata.models) {
      const perVariant = metadata.models[modelKey]?.interiorColorRPO?.[variantId];
      if (perVariant && perVariant[itemId]) return perVariant[itemId];
    }
  }
  for (const modelKey in metadata.models) {
    const model = metadata.models[modelKey];
    if (model.variants) {
      for (const variantKey in model.variants) {
        const variant = model.variants[variantKey];
        if (variantKey === itemId && variant.autoIntelRPOCode) {
          return variant.autoIntelRPOCode;
        }
      }
    }
    if (model.colors) {
      if (model.colors.exterior) {
        for (const colorKey in model.colors.exterior) {
          const color = model.colors.exterior[colorKey];
          if (colorKey === itemId && color.autoIntelRPOCode) {
            return color.autoIntelRPOCode;
          }
        }
      }
      if (model.colors.interior) {
        for (const colorKey in model.colors.interior) {
          const color = model.colors.interior[colorKey];
          if (colorKey === itemId && color.autoIntelRPOCode) {
            return color.autoIntelRPOCode;
          }
        }
      }
    }
    if (model.accessories) {
      for (const accKey in model.accessories) {
        const accessory = model.accessories[accKey];
        // excludeFromRecommendation: the code stays in metadata for other uses
        // (pricing, etc.) but is deliberately withheld from the recommendation API.
        if (accKey === itemId && accessory.autoIntelRPOCode && !accessory.excludeFromRecommendation) {
          return accessory.autoIntelRPOCode;
        }
      }
    }
    if (model.packages) {
      for (const pkgKey in model.packages) {
        const pkg = model.packages[pkgKey];
        if (pkgKey === itemId && pkg.autoIntelRPOCode && !pkg.excludeFromRecommendation) {
          return pkg.autoIntelRPOCode;
        }
      }
    }
  }
  return undefined;
};
